/**
 * Video Carousel — lazy load + focus play
 */
(function () {

  function i18n(root, key, fallback) {
    return (root && root.dataset && root.dataset[key]) || fallback;
  }

  const YOUTUBE_API_SRC = "https://www.youtube.com/iframe_api";
  let youtubeApiPromise = null;
  let youtubeApiFailed = false;

  // #region agent log
  function dbgVc(location, message, data, hypothesisId) {
    fetch("http://127.0.0.1:7455/ingest/c83d4c2b-0051-4caa-8868-891e3a123dcd", {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Debug-Session-Id": "8c28cf" },
      body: JSON.stringify({
        sessionId: "8c28cf",
        location: location,
        message: message,
        data: data || {},
        hypothesisId: hypothesisId,
        timestamp: Date.now(),
      }),
    }).catch(function () {});
  }
  // #endregion

  function youtubePlayerVars(autoplay) {
    const vars = {
      enablejsapi: "1",
      playsinline: "1",
      controls: "0",
      rel: "0",
      modestbranding: "1",
      iv_load_policy: "3",
      disablekb: "1",
      mute: "1",
      origin: location.origin,
    };
    if (autoplay) vars.autoplay = "1";
    return vars;
  }

  function youtubeEmbedSrc(videoId, autoplay) {
    return `https://www.youtube.com/embed/${videoId}?${new URLSearchParams(youtubePlayerVars(autoplay)).toString()}`;
  }

  function loadYouTubeAPI() {
    if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
    if (youtubeApiFailed) return Promise.reject(new Error("api unavailable"));
    if (youtubeApiPromise) return youtubeApiPromise;

    youtubeApiPromise = new Promise((resolve, reject) => {
      let settled = false;
      const succeed = () => {
        if (settled || !(window.YT && window.YT.Player)) return;
        settled = true;
        resolve(window.YT);
      };
      const fail = (reason) => {
        if (settled) return;
        settled = true;
        youtubeApiFailed = true;
        youtubeApiPromise = null;
        reject(reason instanceof Error ? reason : new Error(String(reason)));
      };

      const existing = document.querySelector('script[src*="youtube.com/iframe_api"]');
      if (existing) {
        if (existing.dataset.vcYtFailed === "1") return fail("existing script previously failed");
        existing.addEventListener(
          "error",
          () => {
            existing.dataset.vcYtFailed = "1";
            fail("existing script error");
          },
          { once: true },
        );
        const poll = (left) => {
          if (window.YT && window.YT.Player) return succeed();
          if (left <= 0) return fail("existing script timeout");
          setTimeout(() => poll(left - 1), 50);
        };
        poll(120);
        return;
      }

      const script = document.createElement("script");
      script.src = YOUTUBE_API_SRC;
      script.async = true;
      script.onerror = () => {
        script.dataset.vcYtFailed = "1";
        fail("script onerror");
      };
      document.head.appendChild(script);
      const prevReady = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (typeof prevReady === "function") prevReady();
        succeed();
      };
      const poll = (left) => {
        if (window.YT && window.YT.Player) return succeed();
        if (left <= 0) return fail("script timeout");
        setTimeout(() => poll(left - 1), 50);
      };
      poll(120);
    });

    return youtubeApiPromise;
  }

  function isAppleTouch() {
    return /iP(hone|ad|od)/.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  }

  function documentCanFullscreen() {
    return !!(document.fullscreenEnabled || document.webkitFullscreenEnabled);
  }

  function needsCssFullscreenFallback() {
    if (isAppleTouch()) return true;
    if (documentCanFullscreen()) return false;
    return "ontouchend" in document;
  }

  function instagramEmbedUrl(url) {
    const raw = String(url || "").trim();
    if (!raw) return "";
    if (/\/embed\/?(\?|$)/i.test(raw)) return raw;
    const match = raw.match(/instagram\.com\/(?:[\w.]+\/)?(p|reel|reels|tv)\/([A-Za-z0-9_-]+)/i);
    if (!match) return "";
    const kind = match[1].toLowerCase() === "reel" || match[1].toLowerCase() === "reels" ? "reel" : "p";
    return `https://www.instagram.com/${kind}/${match[2]}/embed/?hidecaption=1&cr=1`;
  }

  /**
   * maxresdefault.jpg 对部分视频不存在，失败后移除 picture 的 source 并回落到 mqdefault.jpg
   */
  function bindYouTubeThumbFallback(root) {
    root.querySelectorAll("img[data-yt-thumb-fallback]").forEach((img) => {
      const applyFallback = () => {
        const fallback = img.dataset.ytThumbFallback;
        img.removeAttribute("data-yt-thumb-fallback");
        const picture = img.parentElement;
        if (picture && picture.tagName === "PICTURE") {
          picture.querySelectorAll("source").forEach((source) => source.remove());
        }
        img.src = fallback;
      };

      if (img.complete) {
        if (img.naturalWidth === 0) applyFallback();
        else if (img.naturalWidth <= 120) applyFallback();
        return;
      }
      img.addEventListener("error", applyFallback, { once: true });
      img.addEventListener(
        "load",
        () => {
          if (img.naturalWidth <= 120) applyFallback();
        },
        { once: true },
      );
    });
  }

  function getBreakpointKey() {
    if (window.matchMedia("(max-width: 374px)").matches) return "mobileLow";
    if (window.matchMedia("(max-width: 767px)").matches) return "mobile";
    if (window.matchMedia("(max-width: 1279px)").matches) return "tablet";
    return "pc";
  }

  const BREAKPOINT_ATTR = {
    pc: "pc",
    tablet: "tablet",
    mobile: "mobile",
    mobileLow: "mobile-low",
  };

  function isPcPointerDevice() {
    return window.matchMedia("(min-width: 1280px) and (hover: hover)").matches;
  }

  class VideoCarouselSlide {
    get isExternalClick() {
      return (
        this.el.dataset.clickBehavior === "external" ||
        this.carousel?.dataset?.clickBehavior === "external"
      );
    }

    constructor(el, carousel) {
      this.el = el;
      this.carousel = carousel;
      this.breakpointKey = getBreakpointKey();
      this.loaded = false;
      this.loadedBreakpoint = null;
      this.player = null;
      this.iframe = null;
      this.video = null;
      this.pendingPlay = false;
      this.pendingRestart = false;
      this.userPaused = false;
      this.ended = false;
      this.ytErrored = false;
      this.awaitingPlayback = false;
      this.hideToggleTimer = null;
      this.mediaEl = el.querySelector(".video-carousel__media");
      this.playToggle = el.querySelector("[data-play-toggle]");
      this.posterEl = el.querySelector("img.video-carousel__poster");
      this.hitEl = el.querySelector("[data-media-hit]");
      this.bezelEl = el.querySelector("[data-bezel]");
      this.progressEl = el.querySelector("[data-progress]");
      this.progressFill = el.querySelector("[data-progress-fill]");
      this.progressTime = el.querySelector("[data-progress-time]");
      this.chromeEl = el.querySelector("[data-chrome]");
      this.fsBtn = el.querySelector("[data-fullscreen]");
      this.progressRaf = 0;
      this.seeking = false;
      this.watchPosterLoad();
      this.syncSource();
      if (!this.isExternalClick) {
        this.bindPlayToggle();
        this.bindToggleInteraction();
        this.bindHit();
        this.bindProgress();
        this.bindFullscreen();
      }

    }

    isLocal() {
      return this.type === "local";
    }

    isControllable() {
      return this.type === "local" || this.type === "youtube";
    }

    isInstagram() {
      return this.type === "instagram";
    }

    /**
     * 高清封面真正解码完成后才淡入，盖住底下的模糊 LQIP
     */
    watchPosterLoad() {
      const img = this.posterEl;
      if (!img) return;

      const markLoaded = () => {
        this.el.classList.add("is-poster-loaded");
      };

      if (img.complete && img.naturalWidth > 0) {
        markLoaded();
        return;
      }
      img.addEventListener("load", markLoaded, { once: true });
    }

    /**
     * 把仍是 lazy 的高清封面提升为立即加载，用于提前拉取邻近 slide
     */
    prefetchPoster() {
      const img = this.posterEl;
      if (!img || img.dataset.vcPrefetched) return;
      img.dataset.vcPrefetched = "1";
      if (img.getAttribute("loading") === "lazy") {
        img.setAttribute("loading", "eager");
      }
    }

    hasSource() {
      switch (this.type) {
        case "local":
          return !!this.videoSrc;
        case "youtube":
          return !!this.youtubeId;
        case "instagram":
          return !!this.instagramUrl;
        default:
          return false;
      }
    }

    bindPlayToggle() {
      if (!this.playToggle) return;
      this.playToggle.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (this.carousel.swipeMoved) return;
        if (this.isInstagram()) {
          this.carousel.openInstagramDialog(this.instagramUrl, this.posterEl);
          return;
        }
        if (!this.isControllable()) return;
        this.togglePlayback();
      });
    }

    showPlayToggle() {
      if (!this.playToggle || this.playToggle.hidden) return;
      clearTimeout(this.hideToggleTimer);
      this.playToggle.classList.remove("is-idle");
    }

    scheduleHidePlayToggle() {
      if (!this.playToggle || this.playToggle.hidden) return;
      if (!this.el.classList.contains("is-playing")) return;

      clearTimeout(this.hideToggleTimer);
      this.hideToggleTimer = setTimeout(() => {
        this.playToggle.classList.add("is-idle");
      }, 1000);
    }

    bindToggleInteraction() {
      if (!this.mediaEl) return;

      this.mediaEl.addEventListener("click", (event) => {
        if (!this.isInstagram()) return;
        if (this.carousel.swipeMoved) return;
        if (event.target.closest("[data-play-toggle]")) return;
        this.carousel.openInstagramDialog(this.instagramUrl, this.posterEl);
      });
    }

    bindHit() {
      if (!this.hitEl) return;
      this.hitEl.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (this.carousel.swipeMoved) return;
        if (this.isInstagram()) return;
        if (!this.isControllable()) return;
        this.togglePlayback();
      });
    }

    bindProgress() {
      if (!this.progressEl) return;

      const onPointer = (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.seekFromPointer(event);
      };

      this.progressEl.addEventListener("pointerdown", (event) => {
        if (!this.isControllable()) return;
        this.seeking = true;
        if (this.progressEl.setPointerCapture) this.progressEl.setPointerCapture(event.pointerId);
        onPointer(event);
      });
      this.progressEl.addEventListener("pointermove", (event) => {
        if (!this.seeking) return;
        onPointer(event);
      });
      const endSeek = () => {
        this.seeking = false;
      };
      this.progressEl.addEventListener("pointerup", endSeek);
      this.progressEl.addEventListener("pointercancel", endSeek);
      this.progressEl.addEventListener("keydown", (event) => {
        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
        event.preventDefault();
        const delta = event.key === "ArrowRight" ? 0.05 : -0.05;
        this.seekToRatio(Math.min(1, Math.max(0, this.getProgressRatio() + delta)));
      });
    }

    bindFullscreen() {
      if (!this.fsBtn) return;
      let last = 0;
      const onFs = (event) => {
        event.preventDefault();
        event.stopPropagation();
        const now = Date.now();
        if (now - last < 350) return;
        last = now;
        this.toggleFullscreen();
      };
      this.fsBtn.addEventListener("click", onFs);
      this.fsBtn.addEventListener("pointerup", onFs);
      this._onFsChange = () => this.syncFullscreenState();
      document.addEventListener("fullscreenchange", this._onFsChange);
      document.addEventListener("webkitfullscreenchange", this._onFsChange);
    }

    bindIOSVideoFullscreen(video) {
      if (!video) return;
      video.addEventListener("webkitbeginfullscreen", () => {
        this.saveFullscreenContext();
        this.syncFullscreenState();
      });
      video.addEventListener("webkitendfullscreen", () => {
        this.captureFullscreenPlayback();
        this.restoreFullscreenContext();
        this.syncFullscreenState();
      });
    }

    usesIOSVideoFullscreen() {
      return !!(this.video && typeof this.video.webkitEnterFullscreen === "function" && isAppleTouch());
    }

    usesIOSCssFullscreen() {
      return needsCssFullscreenFallback() && !this.usesIOSVideoFullscreen();
    }

    lockIOSFsAncestors() {
      this._iosFsAncs = [];
      let node = this.mediaEl && this.mediaEl.parentElement;
      while (node && node !== document.documentElement) {
        node.classList.add("video-carousel-ios-fs-anc");
        this._iosFsAncs.push(node);
        node = node.parentElement;
      }
    }

    unlockIOSFsAncestors() {
      (this._iosFsAncs || []).forEach((node) => {
        node.classList.remove("video-carousel-ios-fs-anc");
      });
      this._iosFsAncs = [];
    }

    applyIOSFsBox() {
      if (!this.mediaEl) return;
      this.mediaEl.style.position = "fixed";
      this.mediaEl.style.top = "0";
      this.mediaEl.style.left = "0";
      this.mediaEl.style.right = "0";
      this.mediaEl.style.bottom = "0";
      this.mediaEl.style.width = "100vw";
      this.mediaEl.style.height = "100vh";
      this.mediaEl.style.height = "100dvh";
      this.mediaEl.style.maxWidth = "none";
      this.mediaEl.style.maxHeight = "none";
      this.mediaEl.style.zIndex = "2147483000";
      this.mediaEl.style.transform = "none";
    }

    clearIOSFsBox() {
      if (!this.mediaEl) return;
      ["position", "top", "left", "right", "bottom", "width", "height", "maxWidth", "maxHeight", "zIndex", "transform"].forEach((prop) => {
        this.mediaEl.style[prop] = "";
      });
    }

    bindIOSFsViewport() {
      if (this._onIosFsVv) return;
      this._onIosFsVv = () => {
        if (this._iosCssFs) this.applyIOSFsBox();
      };
      window.addEventListener("orientationchange", this._onIosFsVv);
    }

    unbindIOSFsViewport() {
      if (!this._onIosFsVv) return;
      window.removeEventListener("orientationchange", this._onIosFsVv);
      this._onIosFsVv = null;
    }

    snapshotIOSFs(step) {
      const wrap = this.el.querySelector(".video-carousel__poster-wrap");
      const rect = this.mediaEl ? this.mediaEl.getBoundingClientRect() : null;
      const wrapStyle = wrap ? getComputedStyle(wrap) : null;
      return {
        step: step,
        playing: this.el.classList.contains("is-playing"),
        userPaused: this.el.classList.contains("is-user-paused"),
        posterVis: wrapStyle ? wrapStyle.visibility : "",
        posterOp: wrapStyle ? wrapStyle.opacity : "",
        playerHidden: this.playerHidden(),
        w: rect ? Math.round(rect.width) : 0,
        h: rect ? Math.round(rect.height) : 0,
        t: rect ? Math.round(rect.top) : 0,
        l: rect ? Math.round(rect.left) : 0,
      };
    }

    enterIOSCssFullscreen() {
      if (!this.mediaEl || this._iosCssFs) return;
      this._iosCssFs = true;
      this.saveFullscreenContext();
      // #region agent log
      const before = this.snapshotIOSFs("before-pin");
      // #endregion
      this.el.classList.add("is-ios-fs");
      this.mediaEl.classList.add("is-ios-fs");
      document.documentElement.classList.add("video-carousel-ios-fs");
      this.applyIOSFsBox();
      this.lockIOSFsAncestors();
      this.bindIOSFsViewport();
      this.syncFullscreenState();
      // #region agent log
      dbgVc("video-carousel.js.liquid:enterIOSCssFullscreen", "enterIOSCssFullscreen", {
        before: before,
        after: this.snapshotIOSFs("after-pin"),
        anc: (this._iosFsAncs || []).length,
      }, "H1");
      // #endregion
    }

    exitIOSCssFullscreen() {
      if (!this._iosCssFs && !(this.mediaEl && this.mediaEl.classList.contains("is-ios-fs"))) return;
      this.captureFullscreenPlayback();
      this._iosCssFs = false;
      this.unbindIOSFsViewport();
      this.clearIOSFsBox();
      this.el.classList.remove("is-ios-fs");
      if (this.mediaEl) this.mediaEl.classList.remove("is-ios-fs");
      this.unlockIOSFsAncestors();
      document.documentElement.classList.remove("video-carousel-ios-fs");
      this.restoreFullscreenContext();
      this.syncFullscreenState();
    }

    saveFullscreenContext() {
      const times = this.getPlaybackTimes();
      this._fsCtx = {
        x: window.scrollX,
        y: window.scrollY,
        track: this.carousel.track ? this.carousel.track.scrollLeft : 0,
        focus: this.carousel.focusedIndex,
        time: times.current || 0,
        duration: times.duration || 0,
        playing: this.el.classList.contains("is-playing") || !this.userPaused,
      };
      this.carousel._fsSlide = this;
      this.carousel._fsHold = true;
    }

    captureFullscreenPlayback() {
      if (!this._fsCtx) return;
      const times = this.getPlaybackTimes();
      if (times.current > 0) this._fsCtx.time = times.current;
      if (times.duration > 0) this._fsCtx.duration = times.duration;
      this._fsCtx.playing = this.el.classList.contains("is-playing") || !this.userPaused;
    }

    holdFullscreenGuard(ms) {
      this.carousel._fsHold = true;
      this.carousel._fsSlide = this;
      clearTimeout(this.carousel._fsHoldTimer);
      this.carousel._fsHoldTimer = setTimeout(() => {
        this.carousel._fsHold = false;
        this.carousel._fsSlide = null;
        this._fsCtx = null;
      }, ms);
    }

    restoreFullscreenContext() {
      const ctx = this._fsCtx;
      if (!ctx) return;
      this.holdFullscreenGuard(420);
      this.pendingRestart = false;
      this.ended = false;
      this.el.classList.remove("is-ended");

      const applyLayout = () => {
        window.scrollTo(ctx.x, ctx.y);
        if (this.carousel.track) {
          this.carousel.track.style.scrollBehavior = "auto";
          this.carousel.track.scrollLeft = ctx.track;
          this.carousel.track.style.scrollBehavior = "";
        }
        this.el.scrollIntoView({ block: "nearest", inline: "nearest" });
      };

      const applyPlayback = () => {
        this.pendingRestart = false;
        if (ctx.time > 0.15) {
          if (this.video && isFinite(this.video.duration) && this.video.duration > 0) {
            if (Math.abs((this.video.currentTime || 0) - ctx.time) > 0.3) {
              this.video.currentTime = ctx.time;
            }
          } else if (this.player && this.player.seekTo) {
            this.player.seekTo(ctx.time, true);
          }
          this.setProgressRatio(ctx.duration ? ctx.time / ctx.duration : 0);
        }
        if (ctx.playing && !this.userPaused) this.play();
      };

      applyLayout();
      applyPlayback();
      requestAnimationFrame(() => {
        applyLayout();
        applyPlayback();
      });
      setTimeout(() => {
        applyLayout();
        applyPlayback();
      }, 80);
      setTimeout(() => {
        applyLayout();
        applyPlayback();
      }, 240);
    }

    isFullscreen() {
      if (this._iosCssFs || (this.mediaEl && this.mediaEl.classList.contains("is-ios-fs"))) return true;
      if (this.video && this.video.webkitDisplayingFullscreen) return true;
      const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
      return fsEl === this.mediaEl || (this.mediaEl && this.mediaEl.contains(fsEl));
    }

    toggleFullscreen() {
      const path = this.usesIOSVideoFullscreen()
        ? "ios-video"
        : this.usesIOSCssFullscreen()
          ? "ios-css"
          : "native";
      // #region agent log
      dbgVc("video-carousel.js.liquid:toggleFullscreen", "toggleFullscreen", {
        type: this.type,
        appleTouch: isAppleTouch(),
        fsEnabled: documentCanFullscreen(),
        needsCss: needsCssFullscreenFallback(),
        hasVideo: !!this.video,
        iosVideoFs: this.usesIOSVideoFullscreen(),
        iosCssFs: this.usesIOSCssFullscreen(),
        isFs: this.isFullscreen(),
        path: path,
        ua: String(navigator.userAgent || "").slice(0, 120),
      }, "G");
      // #endregion
      if (this.usesIOSVideoFullscreen()) {
        if (this.video.webkitDisplayingFullscreen) {
          this.captureFullscreenPlayback();
          if (typeof this.video.webkitExitFullscreen === "function") this.video.webkitExitFullscreen();
        } else {
          this.saveFullscreenContext();
          this.video.webkitEnterFullscreen();
        }
        return;
      }
      if (this.usesIOSCssFullscreen()) {
        if (this.isFullscreen()) this.exitIOSCssFullscreen();
        else this.enterIOSCssFullscreen();
        return;
      }
      if (this.isFullscreen()) {
        if (this._iosCssFs) {
          this.exitIOSCssFullscreen();
          return;
        }
        this.captureFullscreenPlayback();
        const exit = document.exitFullscreen || document.webkitExitFullscreen;
        if (exit) exit.call(document);
        return;
      }
      const target = this.mediaEl;
      if (!target) return;
      this.saveFullscreenContext();
      const req = target.requestFullscreen || target.webkitRequestFullscreen;
      if (req && documentCanFullscreen()) {
        const result = req.call(target);
        if (result && typeof result.then === "function") {
          result.then(() => {
            if (!this.isFullscreen()) this.enterIOSCssFullscreen();
          }).catch(() => {
            this.enterIOSCssFullscreen();
          });
        } else if (!this.isFullscreen()) {
          this.enterIOSCssFullscreen();
        }
      } else if (this.video && this.video.webkitEnterFullscreen) {
        this.video.webkitEnterFullscreen();
      } else {
        this.enterIOSCssFullscreen();
      }
    }

    syncFullscreenState() {
      const on = this.isFullscreen();
      if (this._wasFullscreen && !on) {
        this.captureFullscreenPlayback();
        this.restoreFullscreenContext();
      }
      this._wasFullscreen = on;
      this.el.classList.toggle("is-fullscreen", on);
      if (this.mediaEl) this.mediaEl.classList.toggle("is-fullscreen", on);
      if (this.fsBtn) {
        this.fsBtn.setAttribute(
          "aria-label",
          on
            ? i18n(this.carousel, "i18nExitFullscreen", "Exit fullscreen")
            : i18n(this.carousel, "i18nFullscreen", "Fullscreen"),
        );
      }
    }

    formatTime(seconds) {
      if (!isFinite(seconds) || seconds < 0) return "0:00";
      const total = Math.floor(seconds);
      const s = total % 60;
      const m = Math.floor(total / 60);
      return m + ":" + String(s).padStart(2, "0");
    }

    getPlaybackTimes() {
      if (this.video && this.video.duration) {
        return { current: this.video.currentTime || 0, duration: this.video.duration };
      }
      if (this.player && this.player.getCurrentTime && this.player.getDuration) {
        const duration = this.player.getDuration();
        if (duration > 0) return { current: this.player.getCurrentTime() || 0, duration: duration };
      }
      return { current: 0, duration: 0 };
    }

    playerHidden() {
      const player = this.el.querySelector(".video-carousel__player");
      return !!(player && getComputedStyle(player).visibility === "hidden");
    }

    getProgressRatio() {
      if (this.ended) return 1;
      const times = this.getPlaybackTimes();
      if (times.duration > 0) return times.current / times.duration;
      return 0;
    }

    setProgressRatio(ratio) {
      const safe = Math.min(1, Math.max(0, Number(ratio) || 0));
      if (this.progressFill) this.progressFill.style.transform = "scaleX(" + safe + ")";
      if (this.progressEl) this.progressEl.setAttribute("aria-valuenow", String(Math.round(safe * 100)));
      if (this.progressTime) {
        const times = this.getPlaybackTimes();
        const current = this.ended && times.duration ? times.duration : times.current;
        this.progressTime.textContent = this.formatTime(current) + " / " + this.formatTime(times.duration);
      }
    }

    seekFromPointer(event) {
      const rect = this.progressEl.getBoundingClientRect();
      if (!rect.width) return;
      const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
      this.seekToRatio(ratio);
    }

    seekToRatio(ratio) {
      const safe = Math.min(1, Math.max(0, Number(ratio) || 0));
      this.setProgressRatio(safe);
      if (this.ended && safe < 1) {
        this.ended = false;
        this.el.classList.remove("is-ended");
      }
      if (this.video && this.video.duration) {
        this.video.currentTime = safe * this.video.duration;
      } else if (this.player && this.player.seekTo && this.player.getDuration) {
        const dur = this.player.getDuration();
        if (dur > 0) this.player.seekTo(safe * dur, true);
      }
    }

    updateProgress() {
      if (this.seeking) return;
      this.setProgressRatio(this.getProgressRatio());
      if (this.carousel._fsSlide === this && this._fsCtx) {
        const times = this.getPlaybackTimes();
        if (times.current > 0) this._fsCtx.time = times.current;
        if (times.duration > 0) this._fsCtx.duration = times.duration;
      }
    }

    startProgressWatch() {
      this.stopProgressWatch();
      this.updateProgress();
      if (this.type !== "youtube") return;
      const tick = () => {
        this.updateProgress();
        if (this.el.classList.contains("is-playing") && this.type === "youtube") {
          this.progressRaf = requestAnimationFrame(tick);
        }
      };
      this.progressRaf = requestAnimationFrame(tick);
    }

    stopProgressWatch() {
      if (this.progressRaf) cancelAnimationFrame(this.progressRaf);
      this.progressRaf = 0;
    }

    setLoadingState(isLoading) {
      this.el.classList.toggle("is-loading", isLoading);
    }

    setPlayingState(isPlaying) {
      this.setLoadingState(false);
      if (isPlaying) {
        this.userPaused = false;
        this.ended = false;
        this.awaitingPlayback = false;
        this.pendingRestart = false;
        this.el.classList.add("is-playing");
        this.el.classList.remove("is-user-paused", "is-ended");
      } else if (this.ended && this.el.classList.contains("is-focused")) {
        this.el.classList.remove("is-playing", "is-user-paused");
        this.el.classList.add("is-ended");
      } else if (this.userPaused && this.el.classList.contains("is-focused")) {
        this.el.classList.remove("is-playing", "is-ended");
        this.el.classList.add("is-user-paused");
      } else {
        this.el.classList.remove("is-playing", "is-user-paused");
      }

      if (!this.playToggle) {
        if (isPlaying) this.startProgressWatch();
        else this.stopProgressWatch();
        return;
      }
      this.playToggle.setAttribute("aria-pressed", isPlaying ? "true" : "false");
      this.playToggle.setAttribute(
        "aria-label",
        isPlaying
          ? i18n(this.carousel, "i18nPause", "Pause video")
          : i18n(this.carousel, "i18nPlay", "Play video"),
      );

      if (isPlaying) {
        this.playToggle.classList.add("is-idle");
        this.startProgressWatch();
      } else if (this.ended && this.el.classList.contains("is-focused")) {
        this.stopProgressWatch();
        this.setProgressRatio(1);
        this.showPlayToggle();
        this.playToggle.setAttribute("aria-label", i18n(this.carousel, "i18nReplay", "Replay video"));
      } else if (this.userPaused && this.el.classList.contains("is-focused")) {
        this.stopProgressWatch();
        // #region agent log
        dbgVc("video-carousel.js.liquid:setPlayingState", "user-paused progress", {
          times: this.getPlaybackTimes(),
          playerHidden: this.playerHidden(),
          progressText: this.progressTime ? this.progressTime.textContent : "",
        }, "A");
        // #endregion
        this.updateProgress();
        this.showPlayToggle();
      } else {
        this.stopProgressWatch();
        clearTimeout(this.hideToggleTimer);
        this.playToggle.classList.add("is-idle");
      }
    }

    markEnded() {
      this.ended = true;
      this.userPaused = false;
      this.pendingPlay = false;
      this.awaitingPlayback = false;
      this.setPlayingState(false);
      this.el.classList.add("is-ended");
      this.showPlayToggle();
      if (this.playToggle) this.playToggle.setAttribute("aria-label", i18n(this.carousel, "i18nReplay", "Replay video"));
    }

    togglePlayback() {
      if (this.isExternalClick) return;
      if (this.el.classList.contains("is-playing")) {
        this.pause({ user: true });
      } else if (this.ended || this.el.classList.contains("is-ended")) {
        this.play({ restart: true });
      } else {
        this.play();
      }
    }

    updatePlayToggleVisibility() {
      if (!this.playToggle) return;
      if (this.isInstagram()) {
        this.playToggle.hidden = !this.instagramUrl;
        this.playToggle.setAttribute("aria-label", i18n(this.carousel, "i18nInstagram", "Instagram video"));
        this.showPlayToggle();
        return;
      }
      const visible = this.isControllable();
      this.playToggle.hidden = !visible;
      if (!visible || this.el.classList.contains("is-playing") || (!this.userPaused && !this.ended)) {
        clearTimeout(this.hideToggleTimer);
        this.playToggle.classList.add("is-idle");
      } else {
        this.showPlayToggle();
      }
    }

    syncSource() {
      const bp = BREAKPOINT_ATTR[this.breakpointKey];
      const ds = this.el;
      this.type = ds.getAttribute(`data-${bp}-type`) || ds.getAttribute("data-pc-type") || "local";
      this.youtubeId = ds.getAttribute(`data-${bp}-youtube-id`) || ds.getAttribute("data-pc-youtube-id") || "";
      this.instagramUrl = ds.getAttribute(`data-${bp}-instagram-url`) || ds.getAttribute("data-pc-instagram-url") || "";
      this.videoSrc = ds.getAttribute(`data-${bp}-video-src`) || ds.getAttribute("data-pc-video-src") || "";
      this.el.classList.toggle("is-local", this.isLocal());
      this.el.classList.toggle("is-youtube", this.type === "youtube");
      this.el.classList.toggle("is-instagram", this.isInstagram());
      this.updatePlayToggleVisibility();
      if (this.chromeEl) this.chromeEl.hidden = !this.isControllable();
    }

    onBreakpointChange(nextKey) {
      if (nextKey === this.breakpointKey) return;
      const prevType = this.type;
      const prevYoutubeId = this.youtubeId;
      const prevVideoSrc = this.videoSrc;
      const prevInstagram = this.instagramUrl;
      const savedTime = this.getPlaybackTimes().current;
      const wasLoaded = this.loaded;
      const shouldPlay = this.el.classList.contains("is-focused");

      this.breakpointKey = nextKey;
      this.syncSource();

      const sourceChanged =
        this.type !== prevType ||
        this.youtubeId !== prevYoutubeId ||
        this.videoSrc !== prevVideoSrc ||
        this.instagramUrl !== prevInstagram;

      if (!sourceChanged) {
        this.loadedBreakpoint = nextKey;
        return;
      }

      if (savedTime > 0) this._restoreTime = savedTime;
      if (wasLoaded) this.unload();
      if (wasLoaded || shouldPlay) {
        this.load();
        if (shouldPlay && this.carousel.autoplayEnabled && !this.isInstagram() && !this.isExternalClick) this.play();
      }
    }

    load() {
      if (this.isExternalClick) return;
      const bp = this.breakpointKey;
      if (this.loaded && this.loadedBreakpoint === bp) return;

      if (this.loaded) this.unload();

      this.loaded = true;
      this.loadedBreakpoint = bp;

      const player = this.el.querySelector(".video-carousel__player");
      if (!player) return;

      switch (this.type) {
        case "local":
          this.loadLocal(player);
          break;
        case "youtube":
          this.loadYouTube(player);
          break;
        case "instagram":
          /* Instagram 无法程序化播放，卡片只展示封面，点击后走弹窗 */
          break;
      }
    }

    unload() {
      this.pause();
      const player = this.el.querySelector(".video-carousel__player");
      if (!player) return;

      if (this.player && this.type === "youtube") {
        try {
          this.player.destroy();
        } catch (_) {}
        this.player = null;
      }

      player.innerHTML = "";
      this.iframe = null;
      this.video = null;
      this.pendingPlay = false;
      this.pendingRestart = false;
      this.awaitingPlayback = false;
      this.loaded = false;
      this.loadedBreakpoint = null;
      this.ended = false;
      this.el.classList.remove("is-ended");
      this.stopProgressWatch();
      this.setProgressRatio(0);
      this.setPlayingState(false);
    }

    loadLocal(player) {
      const src = this.videoSrc;
      if (!src) return;

      const video = document.createElement("video");
      video.muted = true;
      video.playsInline = true;
      video.setAttribute("playsinline", "");
      video.setAttribute("webkit-playsinline", "");
      video.preload = "metadata";
      video.loop = false;
      video.src = src;
      player.appendChild(video);
      this.video = video;
      this.bindIOSVideoFullscreen(video);

      video.addEventListener("playing", () => {
        requestAnimationFrame(() => {
          if (video.readyState >= 2) {
            this.setPlayingState(true);
          }
        });
      });

      video.addEventListener("waiting", () => this.setLoadingState(true));

      video.addEventListener("timeupdate", () => this.updateProgress());

      video.addEventListener("ended", () => this.markEnded());

      video.addEventListener("pause", () => {
        if (video.ended) return;
        if (this.awaitingPlayback) return;
        if (video.paused) {
          this.setLoadingState(false);
          this.setPlayingState(false);
        }
      });
    }

    loadYouTube(player) {
      const videoId = this.youtubeId;
      if (!videoId) return;

      const iframe = document.createElement("iframe");
      iframe.setAttribute("src", youtubeEmbedSrc(videoId, false));
      iframe.setAttribute("allow", "autoplay; encrypted-media; fullscreen");
      iframe.setAttribute("allowfullscreen", "");
      iframe.setAttribute("title", i18n(this.carousel, "i18nYoutube", "YouTube video"));
      player.appendChild(iframe);
      this.iframe = iframe;


      loadYouTubeAPI().then((YT) => {
        if (!this.iframe || !this.loaded) return;
        // #region agent log
        dbgVc("video-carousel.js.liquid:loadYouTube", "YT.Player init", {
          videoId: videoId,
          alreadyHadPlayer: !!this.player,
        }, "B");
        // #endregion
        this.player = new YT.Player(iframe, {
          events: {
            onReady: () => {
              if (this.pendingPlay) {
                this.pendingPlay = false;
                this.play();
              }
            },
            onStateChange: (event) => {
              // #region agent log
              dbgVc("video-carousel.js.liquid:onStateChange", "YT state", {
                data: event.data,
                times: this.getPlaybackTimes(),
                playerHidden: this.playerHidden(),
                awaitingPlayback: this.awaitingPlayback,
                isPlaying: this.el.classList.contains("is-playing"),
                isLoading: this.el.classList.contains("is-loading"),
              }, "B");
              // #endregion
              if (event.data === YT.PlayerState.PLAYING) {
                this.ytErrored = false;
                this.awaitingPlayback = false;
                this.pendingRestart = false;
                this.setPlayingState(true);
              } else if (event.data === YT.PlayerState.BUFFERING) {
                if (this._iosCssFs || this.carousel._fsHold) return;
                if (!this.ytErrored && !this.el.classList.contains("is-embed-blocked")) {
                  const bufTimes = this.getPlaybackTimes();
                  if (bufTimes.current < 0.5) this.setLoadingState(true);
                }
              } else if (event.data === YT.PlayerState.ENDED) {
                this.awaitingPlayback = false;
                this.markEnded();
              } else if (event.data === YT.PlayerState.PAUSED) {
                if (this.awaitingPlayback || this.pendingRestart) {
                  this.setLoadingState(true);
                  return;
                }
                if (this._iosCssFs || this.carousel._fsHold) {
                  // #region agent log
                  dbgVc("video-carousel.js.liquid:onStateChange", "YT paused during ios-fs ignored", {
                    times: this.getPlaybackTimes(),
                    isPlaying: this.el.classList.contains("is-playing"),
                    userPaused: this.userPaused,
                  }, "H2");
                  // #endregion
                  return;
                }
                this.setPlayingState(false);
              }
            },
          },
        });
      }).catch((err) => {
        if (this.pendingPlay) {
          this.pendingPlay = false;
          this.applyYouTubeIframeFallback(true);
        } else {
          this.setLoadingState(false);
        }
      });
    }

    /**
     * IFrame API 不可用时，靠重载 embed URL 的 autoplay 控制播放/从头开始
     */
    applyYouTubeIframeFallback(autoplay) {
      if (!this.iframe || !this.youtubeId) {
        this.setLoadingState(false);
        return;
      }

      const nextSrc = youtubeEmbedSrc(this.youtubeId, autoplay);

      if (autoplay) {
        this.setLoadingState(true);
        const onLoad = () => {
          this.pendingRestart = false;
          this.awaitingPlayback = false;
          this.setPlayingState(true);
        };
        this.iframe.addEventListener("load", onLoad, { once: true });
        this.iframe.src = nextSrc;
      } else {
        this.pendingRestart = true;
        this.setPlayingState(false);
        this.iframe.src = youtubeEmbedSrc(this.youtubeId, false);
      }
    }

    play(options = {}) {
      if (this.isExternalClick) return;
      const fromEnded = this.ended || this.el.classList.contains("is-ended");
      // #region agent log
      dbgVc("video-carousel.js.liquid:play", "play()", {
        restart: options.restart === true,
        fromEnded: fromEnded,
        times: this.getPlaybackTimes(),
        restoreTime: this._restoreTime || 0,
        hasPlayVideo: !!(this.player && this.player.playVideo),
        playerHidden: this.playerHidden(),
        loaded: this.loaded,
        willLoad: !this.loaded,
      }, "C");
      // #endregion
      const restart = options.restart === true || fromEnded;
      if (restart) this.pendingRestart = true;
      this.ended = false;
      this.el.classList.remove("is-ended");
      this.userPaused = false;
      this.el.classList.remove("is-user-paused");
      this.awaitingPlayback = true;

      if (!this.loaded) this.load();

      if (
        this.hasSource() &&
        !this.isInstagram() &&
        !this.el.classList.contains("is-embed-blocked") &&
        this.getPlaybackTimes().current < 0.5
      ) {
        this.setLoadingState(true);
      }


      switch (this.type) {
        case "local":
          if (this.video) {
            if (this.pendingRestart) {
              this.video.currentTime = 0;
              this.pendingRestart = false;
            } else if (this._restoreTime > 0) {
              this.video.currentTime = this._restoreTime;
              this._restoreTime = 0;
            }
            this.video.play().catch(() => {});
          }
          break;
        case "youtube":
          if (this.el.classList.contains("is-embed-blocked")) {
            this.awaitingPlayback = false;
            this.setLoadingState(false);
            break;
          }
          if (this.player && this.player.playVideo) {
            if (this.pendingRestart && this.player.seekTo) {
              this.player.seekTo(0, true);
            } else if (this._restoreTime > 0 && this.player.seekTo) {
              this.player.seekTo(this._restoreTime, true);
              this._restoreTime = 0;
            }
            this.player.playVideo();
          } else if (youtubeApiFailed) {
            this.applyYouTubeIframeFallback(true);
          } else {
            /* YT.Player 尚未就绪，标记待播，onReady 或 API 失败兜底时补上 */
            this.pendingPlay = true;
          }
          break;
        case "instagram":
          /* Instagram 不支持程序化自动播放，聚焦时仅加载 embed */
          break;
      }
    }

    pause(options = {}) {
      const reset = options.reset === true;
      // #region agent log
      dbgVc("video-carousel.js.liquid:pause", "pause()", {
        user: options.user === true,
        reset: reset,
        times: this.getPlaybackTimes(),
        playerHidden: this.playerHidden(),
        hasPauseVideo: !!(this.player && this.player.pauseVideo),
        isPlaying: this.el.classList.contains("is-playing"),
      }, "A");
      // #endregion
      this.userPaused = options.user === true;
      this.awaitingPlayback = false;
      if (!this.userPaused) this.el.classList.remove("is-user-paused");
      this.setLoadingState(false);
      if (this.userPaused) this.setPlayingState(false);

      switch (this.type) {
        case "local":
          if (this.video) {
            this.video.pause();
            if (reset) {
              this.video.currentTime = 0;
              this.pendingRestart = true;
            }
          }
          break;
        case "youtube":
          this.pendingPlay = false;
          if (this.player && this.player.pauseVideo) {
            this.player.pauseVideo();
            if (reset && this.player.seekTo) {
              this.player.seekTo(0, true);
              this.pendingRestart = true;
            }
          } else if (youtubeApiFailed) {
            this.applyYouTubeIframeFallback(false);
          } else if (reset) {
            this.pendingRestart = true;
          }
          break;
        case "instagram":
          this.el.classList.remove("is-playing");
          break;
      }
    }
  }

  class VideoCarousel extends HTMLElement {
    get autoplayEnabled() {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
      return this.dataset.autoplay !== "false";
    }

    get hasPlayIcons() {
      return this.dataset.hasPlayIcons === "true";
    }

    get loopEnabled() {
      return this.dataset.loop === "true" && this.slideEls?.length > 1;
    }

    connectedCallback() {
      this.track = this.querySelector("image-link-blocks");
      if (!this.track) return;

      bindYouTubeThumbFallback(this);

      this.slideEls = [...this.track.querySelectorAll("[data-video-carousel-slide]")];
      this.slides = this.slideEls.map((el) => new VideoCarouselSlide(el, this));
      this.focusedIndex = 0;
      this.scrollTimer = null;
      this._lastNavAt = 0;
      this.breakpointKey = getBreakpointKey();

      this.slideEls.forEach((el, i) => {
        el.classList.toggle("is-focused", i === 0);
      });

      this._lockFocusIndex = null;
      this._normalizing = false;
      this.setupInfiniteLoop();
      this.setupIntersectionObserver();
      this.setupScrollListener();
      this.setupBreakpointListener();
      this.setupControlsVisibility();
      this.setupSwipeGuard();
      this.setupAlignedNavigation();
      this.setupInstagramDialog();

      this.prefetchAround(0);

      if (this.slides[0] && !this.slides[0].isExternalClick) {
        this.slides[0].load();
        if (this.autoplayEnabled && !this.slides[0].isInstagram()) {
          this.slides[0].play();
        }
      }

    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.scrollTimer) clearTimeout(this.scrollTimer);
      if (this._controlsResizeObserver) this._controlsResizeObserver.disconnect();
      if (this._loopResizeTimer) clearTimeout(this._loopResizeTimer);
      if (this._onBreakpointChange) {
        window.removeEventListener("resize", this._onBreakpointChange);
      }
      if (this._updateControlsVisibility) {
        window.removeEventListener("resize", this._updateControlsVisibility);
      }
      if (this._fsHoldTimer) clearTimeout(this._fsHoldTimer);
      this.slides?.forEach((slide) => {
        if (slide.hideToggleTimer) clearTimeout(slide.hideToggleTimer);
        slide.stopProgressWatch?.();
        if (slide._onFsChange) {
          document.removeEventListener("fullscreenchange", slide._onFsChange);
          document.removeEventListener("webkitfullscreenchange", slide._onFsChange);
        }
      });
    }

    setupSwipeGuard() {
      this.swipeMoved = false;
      this.swipeStartX = 0;
      this.track.addEventListener(
        "pointerdown",
        (event) => {
          this.swipeStartX = event.clientX;
          this.swipeMoved = false;
        },
        { passive: true },
      );
      this.track.addEventListener(
        "pointermove",
        (event) => {
          if (Math.abs(event.clientX - this.swipeStartX) > 8) this.swipeMoved = true;
        },
        { passive: true },
      );
      this.track.addEventListener(
        "scroll",
        () => {
          this.swipeMoved = true;
        },
        { passive: true },
      );
      this.track.addEventListener("click", (event) => {
        if (this.dataset.clickBehavior !== "external") return;
        if (!this.swipeMoved) return;
        const link = event.target.closest("a.video-carousel__external");
        if (!link) return;
        event.preventDefault();
      });
    }

    setupInstagramDialog() {
      this.igDialog = this.querySelector("[data-ig-dialog]");
      this.igDialogMedia = this.querySelector("[data-ig-dialog-media]");
      if (!this.igDialog || !this.igDialogMedia) return;

      const close = () => this.closeInstagramDialog();
      this.igDialog.querySelector("[data-ig-dialog-close]")?.addEventListener("click", close);
      this.igDialog.addEventListener("click", (event) => {
        if (event.target === this.igDialog) close();
      });
      this.igDialog.addEventListener("close", () => {
        this.igDialog.classList.remove("is-pointer-open");
        this.clearInstagramDialog();
      });
      this.igDialog.addEventListener("keydown", (event) => {
        if (event.key === "Tab") this.igDialog.classList.remove("is-pointer-open");
      });
      this._igScrollLock = (event) => event.preventDefault();
      this.igDialogMedia.addEventListener("wheel", this._igScrollLock, { passive: false });
      this.igDialogMedia.addEventListener("touchmove", this._igScrollLock, { passive: false });
    }

    openInstagramDialog(url, posterImg) {
      if (this.swipeMoved) return;
      const embedUrl = instagramEmbedUrl(url);
      const posterSrc = posterImg && (posterImg.currentSrc || posterImg.getAttribute("src") || "");
      if (!embedUrl || !this.igDialog || !this.igDialogMedia) return;

      this.clearInstagramDialog();
      const iframe = document.createElement("iframe");
      iframe.className = "video-carousel__ig-dialog-iframe";
      iframe.src = embedUrl;
      iframe.scrolling = "no";
      iframe.setAttribute("scrolling", "no");
      iframe.setAttribute("allow", "autoplay; clipboard-write; encrypted-media; picture-in-picture; fullscreen");
      iframe.setAttribute("allowfullscreen", "");
      iframe.setAttribute("title", i18n(this, "i18nInstagram", "Instagram video"));
      const hidePoster = (reason) => {
        this.igDialogMedia.classList.add("is-embed-ready");
      };
      this.igDialogMedia.appendChild(iframe);
      if (posterSrc) {
        const poster = document.createElement("img");
        poster.className = "video-carousel__ig-dialog-poster";
        poster.src = posterSrc;
        poster.alt = "";
        poster.setAttribute("aria-hidden", "true");
        this.igDialogMedia.appendChild(poster);
      }

      if (typeof this.igDialog.showModal === "function") this.igDialog.showModal();
      else this.igDialog.setAttribute("open", "");
      this.igDialog.classList.add("is-pointer-open");
      const focusDialog = () => {
        try {
          this.igDialog.focus({ preventScroll: true });
        } catch (_) {
          this.igDialog.focus();
        }
      };
      focusDialog();
      requestAnimationFrame(focusDialog);
    }

    closeInstagramDialog() {
      if (!this.igDialog) return;
      if (typeof this.igDialog.close === "function" && this.igDialog.open) this.igDialog.close();
      else this.igDialog.removeAttribute("open");
      this.clearInstagramDialog();
    }

    clearInstagramDialog() {
      if (!this.igDialogMedia) return;
      this.igDialogMedia.classList.remove("is-embed-ready");
      this.igDialogMedia.innerHTML = "";
    }

    setupControlsVisibility() {
      this.controls = this.querySelector(".video-carousel__controls");
      if (!this.controls) return;

      this._updateControlsVisibility = () => {
        if (!this.controls || !this.track) return;
        const scrollable = this.track.scrollWidth > this.track.clientWidth + 2;
        this.controls.toggleAttribute("hidden", !scrollable);
      };

      this._updateControlsVisibility();
      requestAnimationFrame(() => this._updateControlsVisibility());

      if ("ResizeObserver" in window) {
        this._controlsResizeObserver = new ResizeObserver(() => this._updateControlsVisibility());
        this._controlsResizeObserver.observe(this.track);
      }

      window.addEventListener("resize", this._updateControlsVisibility, { passive: true });
    }

    isAnySlideFullscreen() {
      if (this._fsHold || this._fsSlide) return true;
      return this.slides?.some((slide) => slide.isFullscreen && slide.isFullscreen());
    }

    setupBreakpointListener() {
      this._onBreakpointChange = () => {
        if (this.isAnySlideFullscreen()) return;
        this._resizeHold = true;
        clearTimeout(this._resizeHoldTimer);
        this._resizeHoldTimer = setTimeout(() => {
          this._resizeHold = false;
        }, 400);
        const nextKey = getBreakpointKey();
        if (nextKey !== this.breakpointKey) {
          this.breakpointKey = nextKey;
          this.slides.forEach((slide) => slide.onBreakpointChange(nextKey));
        }
        this._updateControlsVisibility?.();
        if (!this.loopEnabled) return;
        clearTimeout(this._loopResizeTimer);
        this._loopResizeTimer = setTimeout(() => this.rebuildInfiniteLoop(), 120);
      };
      window.addEventListener("resize", this._onBreakpointChange, { passive: true });
    }

    setupIntersectionObserver() {
      if (!("IntersectionObserver" in window)) return;

      this.io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const index = this.slideEls.indexOf(entry.target);
            if (index < 0) return;
            const slide = this.slides[index];

            if (this.isAnySlideFullscreen() || this._resizeHold) return;
            if (entry.isIntersecting) {
              slide.load();
              if (index === this.focusedIndex && this.autoplayEnabled && !slide.isInstagram() && !slide.isExternalClick) slide.play();
            } else {
              slide.pause();
            }
          });
        },
        { root: null, rootMargin: "120px 0px", threshold: 0.01 },
      );

      this.slideEls.forEach((el) => this.io.observe(el));
    }

    setFocusedIndex(index) {
      if (index < 0 || index >= this.slides.length) return;
      if (this.isAnySlideFullscreen()) return;

      const changed = index !== this.focusedIndex;
      this.focusedIndex = index;
      this.syncDots(index);
      this.syncArrowState();
      if (!changed) return;

      this.slides.forEach((slide, i) => {
        const isFocused = i === index;
        slide.el.classList.toggle("is-focused", isFocused);
        if (isFocused) {
          slide.load();
          if (this.autoplayEnabled && !slide.isInstagram() && !slide.isExternalClick) slide.play();
        } else {
          slide.pause();
        }
      });

      this.prefetchAround(index);
    }

    /**
     * 提前拉取邻近 slide 的高清封面，避免滑过去时出现无封面的空窗
     */
    prefetchAround(index) {
      [index, index + 1, index + 2].forEach((i) => this.slides[i]?.prefetchPoster());
    }

    setupScrollListener() {
      const onSettle = () => {
        if (this._normalizing) return;
        if (this.isAnySlideFullscreen()) return;
        if (this.swipeMoved) this._lockFocusIndex = null;
        if (this.loopEnabled) this.normalizeLoop();
        else this.setFocusedIndex(this.getFocusedIndex());
      };

      this.track.addEventListener(
        "scroll",
        () => {
          if (this._normalizing) return;
          if (this.scrollTimer) clearTimeout(this.scrollTimer);
          this.scrollTimer = setTimeout(onSettle, 80);
        },
        { passive: true },
      );

      this.track.addEventListener("scrollend", () => {
        if (this.scrollTimer) clearTimeout(this.scrollTimer);
        onSettle();
      });

      this.track.addEventListener("scroll:change", (e) => {
        const index = e.detail?.index;
        if (typeof index !== "number") return;
        if (this._lockFocusIndex != null || this._normalizing) return;
        this.setFocusedIndex(this.wrapIndex(index));
      });
    }

    /**
     * image-link-blocks / swiper-dots 默认按「居中」滚动，且滚到尽头会强制选中最后一张。
     * 可视区多张时（PC 610）倒数第二张永远到不了居中/start，这里改成 start 对齐并锁住目标 index。
     */
    setupAlignedNavigation() {
      this.track._scrollToIndex = (index) => this.scrollToIndex(index);
      this.track._calculateCurrentIndex = () => this.getFocusedIndex();
      this.track._next = () => this.goNext();
      this.track._prev = () => this.goPrev();
      this.track.addEventListener(
        "control:next",
        (event) => {
          event.stopImmediatePropagation();
          this.goNext();
        },
        true,
      );
      this.track.addEventListener(
        "control:prev",
        (event) => {
          event.stopImmediatePropagation();
          this.goPrev();
        },
        true,
      );

      const dots = this.querySelector("swiper-dots");
      if (dots && typeof dots._scrollToIndex === "function") {
        dots._scrollToIndex = (index) => this.scrollToIndex(index);
      }

      this.prevBtn = this.querySelector('button[is="prev-button"]');
      this.nextBtn = this.querySelector('button[is="next-button"]');
      this.syncArrowState();
      this.track.addEventListener("scroll:edge-nearing", () => this.syncArrowState());
      this.track.addEventListener("scroll:edge-leaving", () => this.syncArrowState());

      this.track.addEventListener(
        "pointerdown",
        (event) => {
          const slideEl = event.target.closest("[data-video-carousel-slide], [data-video-carousel-clone]");
          if (!slideEl) return;
          const index = this.logicalIndexOf(slideEl);
          if (index < 0) return;
          this._lockFocusIndex = index;
          this.setFocusedIndex(index);
        },
        true,
      );

      this.track.addEventListener(
        "pointermove",
        (event) => {
          if (Math.abs(event.clientX - this.swipeStartX) <= 8) return;
          this._lockFocusIndex = null;
        },
        { passive: true },
      );
    }

    wrapIndex(index) {
      const total = this.slideEls.length;
      if (!total) return 0;
      return ((index % total) + total) % total;
    }

    trackPad() {
      return parseFloat(getComputedStyle(this.track).paddingInlineStart) || 0;
    }

    loopCloneCount() {
      const slide = this.slideEls[0];
      if (!slide) return 1;
      const slideWidth = slide.getBoundingClientRect().width;
      const gap = parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap) || 0;
      const step = slideWidth + gap;
      if (step <= 1) return 1;
      return Math.max(1, Math.ceil(this.track.clientWidth / step));
    }

    teardownLoopClones() {
      this._loopPrefix?.forEach((el) => el.remove());
      this._loopSuffix?.forEach((el) => el.remove());
      this._loopPrefix = [];
      this._loopSuffix = [];
      this._loopCloneCount = 0;
      this.classList.remove("is-looping");
    }

    rebuildInfiniteLoop() {
      if (!this.loopEnabled) return;
      const nextCount = Math.min(this.slideEls.length, this.loopCloneCount());
      const sameClones = nextCount === this._loopCloneCount && this._loopPrefix?.length;
      if (sameClones) {
        this.jumpToReal(this.focusedIndex);
        return;
      }
      this.setupInfiniteLoop(this.focusedIndex);
    }

    setupInfiniteLoop(index = 0) {
      if (!this.loopEnabled) return;

      const slideWidth = this.slideEls[0]?.getBoundingClientRect().width || 0;
      if (slideWidth < 1) {
        requestAnimationFrame(() => this.setupInfiniteLoop(index));
        return;
      }

      this.teardownLoopClones();
      this.classList.add("is-looping");
      this._loopPrefix = [];
      this._loopSuffix = [];

      const total = this.slideEls.length;
      const cloneCount = Math.min(total, this.loopCloneCount());
      this._loopCloneCount = cloneCount;

      for (let i = total - cloneCount; i < total; i++) {
        const clone = this.createLoopClone(this.slideEls[i], i);
        this.track.insertBefore(clone, this.slideEls[0]);
        this._loopPrefix.push(clone);
      }
      for (let i = 0; i < cloneCount; i++) {
        const clone = this.createLoopClone(this.slideEls[i], i);
        this.track.appendChild(clone);
        this._loopSuffix.push(clone);
      }

      this.jumpToReal(index);
    }

    createLoopClone(el, logicalIndex) {
      const clone = el.cloneNode(true);
      clone.classList.remove("is-focused", "is-playing", "is-loading", "is-ended");
      clone.setAttribute("data-video-carousel-clone", "");
      clone.setAttribute("data-clone-index", String(logicalIndex));
      clone.setAttribute("aria-hidden", "true");
      clone.removeAttribute("id");
      clone.querySelectorAll("[id]").forEach((node) => node.removeAttribute("id"));
      clone
        .querySelectorAll("video, iframe, [data-play-toggle], [data-media-hit], [data-progress], [data-fullscreen], [data-chrome]")
        .forEach((node) => node.remove());
      return clone;
    }

    logicalIndexOf(el) {
      if (!el) return -1;
      if (el.hasAttribute("data-video-carousel-clone")) {
        const index = parseInt(el.dataset.cloneIndex, 10);
        return Number.isNaN(index) ? -1 : index;
      }
      return this.slideEls.indexOf(el);
    }

    loopNodes() {
      return [...this.track.querySelectorAll("[data-video-carousel-slide]")];
    }

    getAlignedNode() {
      const nodes = this.loopNodes();
      if (!nodes.length) return null;
      const start = this.track.getBoundingClientRect().left + this.trackPad();
      let closest = nodes[0];
      let minDist = Infinity;
      nodes.forEach((el) => {
        const dist = Math.abs(el.getBoundingClientRect().left - start);
        if (dist < minDist) {
          minDist = dist;
          closest = el;
        }
      });
      return closest;
    }

    scrollLeftFor(el) {
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      return Math.max(0, Math.min(el.offsetLeft - this.trackPad(), maxScroll));
    }

    pickLoopTarget(index) {
      const real = this.slideEls[index];
      if (!real) return null;
      if (!this.loopEnabled) return real;

      const last = this.slideEls.length - 1;
      const from = this.focusedIndex;
      if (from === last && index === 0 && this._loopSuffix?.[0]) return this._loopSuffix[0];
      if (from === 0 && index === last && this._loopPrefix?.length) {
        return this._loopPrefix[this._loopPrefix.length - 1];
      }

      const byIndex = (el) => el.dataset.cloneIndex === String(index);
      const candidates = [
        real,
        this._loopPrefix?.find(byIndex),
        this._loopSuffix?.find(byIndex),
      ].filter(Boolean);
      const current = this.track.scrollLeft;
      let best = real;
      let bestDist = Infinity;
      candidates.forEach((el) => {
        const dist = Math.abs(this.scrollLeftFor(el) - current);
        if (dist < bestDist) {
          bestDist = dist;
          best = el;
        }
      });
      return best;
    }

    jumpToReal(index) {
      const el = this.slideEls[index];
      if (!el || !this.track) return;

      this._normalizing = true;
      this.track.style.scrollBehavior = "auto";
      this.track.scrollTo({ left: this.scrollLeftFor(el), behavior: "auto" });
      this._lockFocusIndex = index;
      this.setFocusedIndex(index);
      requestAnimationFrame(() => {
        this.track.style.scrollBehavior = "";
        this._normalizing = false;
      });
    }

    normalizeLoop() {
      if (!this.loopEnabled || this._normalizing) return;
      const aligned = this.getAlignedNode();
      if (!aligned) return;
      const logical = this.logicalIndexOf(aligned);
      if (logical < 0) return;
      this._lockFocusIndex = logical;
      this.setFocusedIndex(logical);
      if (aligned.hasAttribute("data-video-carousel-clone")) {
        this.jumpToReal(logical);
      }
    }

    canNavigate(ms = 480) {
      const now = performance.now();
      if (now - this._lastNavAt < ms) return false;
      this._lastNavAt = now;
      return true;
    }

    goNext() {
      if (!this.slideEls.length) return;
      const next = this.loopEnabled
        ? this.wrapIndex(this.focusedIndex + 1)
        : Math.min(this.focusedIndex + 1, this.slideEls.length - 1);
      this.scrollToIndex(next);
    }

    goPrev() {
      if (!this.slideEls.length) return;
      const prev = this.loopEnabled
        ? this.wrapIndex(this.focusedIndex - 1)
        : Math.max(this.focusedIndex - 1, 0);
      this.scrollToIndex(prev);
    }

    syncArrowState() {
      if (!this.prevBtn && !this.nextBtn) return;
      if (this.loopEnabled) {
        if (this.prevBtn) this.prevBtn.disabled = false;
        if (this.nextBtn) this.nextBtn.disabled = false;
        return;
      }
      const last = this.slideEls.length - 1;
      if (this.prevBtn) this.prevBtn.disabled = this.focusedIndex <= 0;
      if (this.nextBtn) this.nextBtn.disabled = this.focusedIndex >= last;
    }

    scrollToIndex(index) {
      if (!this.canNavigate()) return;
      const el = this.pickLoopTarget(index);
      if (!el || !this.track) return;

      this._lockFocusIndex = index;
      this.setFocusedIndex(index);

      const smooth = window.matchMedia("(prefers-reduced-motion: no-preference)").matches;
      this.track.scrollTo({
        left: this.scrollLeftFor(el),
        behavior: smooth ? "smooth" : "auto",
      });
    }

    syncDots(index) {
      const dots = this.querySelector("swiper-dots");
      if (dots && typeof dots.select === "function") {
        dots.select(index, false);
      }
    }

    getFocusedIndex() {
      if (!this.slideEls.length) return 0;
      if (this._lockFocusIndex != null) return this._lockFocusIndex;
      const aligned = this.getAlignedNode();
      const logical = this.logicalIndexOf(aligned);
      return logical < 0 ? 0 : logical;
    }
  }

  if (!customElements.get("video-carousel")) {
    customElements.define("video-carousel", VideoCarousel);
  }
})();
